import { ApplicationView, useController } from '@aha-app/mvc';
import classNames from 'classnames';
import type { Identifier } from 'dnd-core';
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import PresentationController, {
  type PresentationFrame,
} from '../controllers/PresentationController';
import WhiteboardController from '../controllers/WhiteboardController';
import trackEvent from '../utils/analytics';
import { zoomAndPanTo } from '../utils/zoom';
import FrameThumbnail from './FrameThumbnail';
import styles from './presentations.module.css';

interface DraggableProps {
  id: string;
  index: number;
  doMove: (dragIndex: number, hoverIndex: number) => void;
  children: (attrs: {
    isDragging: boolean;
    ref: React.RefObject<HTMLDivElement>;
  }) => React.ReactElement;
}

interface DragItem {
  index: number;
  id: string;
  type: 'frame';
}

const Draggable: React.FC<DraggableProps> = ({
  id,
  children,
  index,
  doMove,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [, drop] = useDrop<DragItem, null, { handlerId: Identifier | null }>({
    accept: 'frame',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      doMove(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    item: { id, index, type: 'frame' },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return children({ isDragging, ref });
};

interface FrameProps {
  frame: PresentationFrame;
  index: number;
  isDragging: boolean;
  isSelected: boolean;
  setSelected: () => void;
}

const Frame = forwardRef<HTMLDivElement, FrameProps>(function Frame(
  { frame, index, isDragging, isSelected, setSelected },
  ref
) {
  const controller = useController(PresentationController);
  const whiteboardController = useController(WhiteboardController);
  const menuRef = useRef<HTMLAhaMenuElement>();

  const setMenuOpen = useCallback(
    (open: boolean, position: { left: number; top: number }) => {
      if (menuRef.current) {
        menuRef.current.style.left = `${position.left}px`;
        menuRef.current.style.top = `${position.top}px`;
        menuRef.current.setAttribute('open', open ? 'true' : '');
      }
    },
    []
  );

  const { frameEditorItem } = styles;
  return (
    <div
      ref={ref}
      className={classNames(frameEditorItem, {
        [styles.isHidden]: !frame.getVisibility(),
        [styles.isDragging]: isDragging,
      })}
      onContextMenu={e => {
        e.preventDefault();

        // determine where the click was, to position the context menu
        const dims = (e.target as HTMLDivElement)
          .closest(`.${frameEditorItem}`)
          ?.getBoundingClientRect();
        const left = dims ? e.clientX - dims.left : 0;
        const top = dims ? e.clientY - dims.top : 0;
        setMenuOpen(true, { left, top });
      }}
    >
      <a
        className={classNames({
          [styles.selected]: isSelected,
        })}
        onClick={() => {
          zoomAndPanTo(whiteboardController, [frame.frame]);
          setSelected();
        }}
      >
        <FrameThumbnail frame={frame} />

        <h5 className={styles.frameTitle}>{frame.getName()}</h5>
        <span className={styles.frameNumber}>{index + 1}</span>
      </a>

      {frame.getVisibility() && (
        <aha-menu
          ref={menuRef}
          style={{
            position: 'absolute',
          }}
        >
          <div slot='control' />
          <aha-menu-content>
            <aha-menu-item
              onClick={() => {
                controller.toggleFrameVisibility(frame.id, false);
              }}
            >
              Hide frame
            </aha-menu-item>
          </aha-menu-content>
        </aha-menu>
      )}

      {!frame.getVisibility() && (
        <div className={styles.frameHiddenOverlay}>
          <span className={styles.wrapper} />

          <span className={styles.hiddenIcon}>
            <i className='fa-regular fa-eye-slash' />
          </span>

          <span className={styles.visibleIcon}>
            <i className='fa-regular fa-eye' />
          </span>

          <span className={styles.wrapper}>
            <aha-button
              onClick={() => controller.toggleFrameVisibility(frame.id, true)}
              size='small'
            >
              Show frame
            </aha-button>
          </span>
        </div>
      )}
    </div>
  );
});

const SidePanelContent: React.FC = () => {
  const controller = useController(PresentationController);

  const { selectedFrameId } = controller.state;

  useEffect(() => controller.updateOnChange(), [controller]);

  useEffect(() => {
    // we unmount this component on close, so this will fire each time it's opened
    trackEvent(
      'Feature-7298498475865992680',
      'User opens presentation thumbnail view'
    );
  }, []);

  if (!controller.props.dragDropManager) {
    console.warn('No dragDropManager provided to Whiteboard');
    return null;
  }

  return (
    <div className={styles.frameEditorList}>
      {/* @ts-expect-error DndProvider does not type its children */}
      <DndProvider manager={controller.props.dragDropManager}>
        {controller.getFrames(true).map((frame, index) => (
          <Draggable
            id={frame.id}
            index={index}
            key={frame.id}
            doMove={(dragIndex, hoverIndex) =>
              controller.moveFrame(dragIndex, hoverIndex)
            }
          >
            {({ isDragging, ref }) => (
              <Frame
                frame={frame}
                index={index}
                isDragging={isDragging}
                ref={ref}
                isSelected={selectedFrameId === frame.id}
                setSelected={() => controller.actionSetSelectedFrame(frame.id)}
              />
            )}
          </Draggable>
        ))}
      </DndProvider>
    </div>
  );
};

export default ApplicationView(SidePanelContent);
